import { createFileRoute, Link } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { MessageSquare, Users, Send } from 'lucide-react'
import { APP_NAME, APP_VERSION } from '@/lib/constants'
import { useAuth } from '@/hooks/use-auth'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

export const Route = createFileRoute('/_public/')({
  component: HomePage,
})

function HomePage() {
  const { currentUser, signOut } = useAuth()

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-6 w-6 text-slate-900" />
            <span className="font-semibold text-xl">{APP_NAME}</span>
          </div>
          {currentUser ? (
            <div className="flex items-center gap-3">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="rounded-full border border-slate-300 px-3 py-1 text-sm text-slate-700 hover:border-slate-400 hover:text-slate-900"
                  >
                    {currentUser.email}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={() => {
                      void signOut()
                    }}
                  >
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Link to="/app">
                <Button variant="ghost">Open App</Button>
              </Link>
            </div>
          ) : (
            <Link to="/sign-in">
              <Button variant="ghost">Sign In</Button>
            </Link>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-20 md:py-32">
        <div className="max-w-3xl mx-auto text-center space-y-8">
          <h1 className="text-5xl md:text-6xl font-bold tracking-tight text-slate-900">
            Draft it once.
            <br />
            Send it right.
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Send personalized messages to your group — one at a time — without
            creating group texts or using unfamiliar numbers.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/app">
              <Button size="lg" className="text-lg px-8">
                Get Started Free
              </Button>
            </Link>
          </div>
          <p className="text-sm text-slate-500">
            No signup required • Messages open as drafts on your device
          </p>
        </div>
      </section>

      {/* How It Works */}
      <section className="container mx-auto px-4 py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">How It Works</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center space-y-4">
              <div className="w-12 h-12 bg-slate-900 text-white rounded-full flex items-center justify-center mx-auto text-xl font-bold">
                1
              </div>
              <Users className="h-8 w-8 mx-auto text-slate-600" />
              <h3 className="font-semibold text-lg">Import Contacts</h3>
              <p className="text-slate-600 text-sm">
                Add contacts manually, from your phone, paste a list, or upload a CSV file.
              </p>
            </div>
            <div className="text-center space-y-4">
              <div className="w-12 h-12 bg-slate-900 text-white rounded-full flex items-center justify-center mx-auto text-xl font-bold">
                2
              </div>
              <MessageSquare className="h-8 w-8 mx-auto text-slate-600" />
              <h3 className="font-semibold text-lg">Write Your Message</h3>
              <p className="text-slate-600 text-sm">
                Compose once and personalize with each recipient's name.
              </p>
            </div>
            <div className="text-center space-y-4">
              <div className="w-12 h-12 bg-slate-900 text-white rounded-full flex items-center justify-center mx-auto text-xl font-bold">
                3
              </div>
              <Send className="h-8 w-8 mx-auto text-slate-600" />
              <h3 className="font-semibold text-lg">Send Individually</h3>
              <p className="text-slate-600 text-sm">
                Tap each contact to open a personalized draft in your messaging
                app.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="container mx-auto px-4 py-20">
        <div className="max-w-2xl mx-auto text-center space-y-6">
          <h2 className="text-3xl font-bold">Your Privacy Matters</h2>
          <p className="text-slate-600">
            Messages are opened as drafts in your phone or email app.{' '}
            <strong>Nothing is sent automatically.</strong>
          </p>
          <ul className="text-left space-y-2 text-slate-600 max-w-md mx-auto">
            <li>✓ No contacts stored long-term</li>
            <li>✓ No message content saved after your session</li>
            <li>✓ No third-party SMS services</li>
            <li>✓ Messages sent from your own phone number</li>
            <li>✓ No reply-all - replies come only to you</li>
            <li>✓ Phone numbers are hidden - not seen by others</li>
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="container mx-auto px-4 py-20 bg-slate-900 text-white rounded-2xl mb-20">
        <div className="max-w-2xl mx-auto text-center space-y-6">
          <h2 className="text-3xl font-bold">Ready to get started?</h2>
          <p className="text-slate-300">
            Start sending personalized messages in under 2 minutes.
          </p>
          <Link to="/app">
            <Button size="lg" variant="secondary" className="text-lg px-8">
              Launch MessageDrafts
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t py-8">
        <div className="container mx-auto px-4 text-center text-slate-600 text-sm">
          <p>© 2026 {APP_NAME} - v{APP_VERSION}. All rights reserved.</p>
        </div>
      </footer>
    </div>
  )
}
